export const ASK_HELIX_PERSONA_LENS_IDS = [
  'ceo',
  'cfo',
  'coo',
  'chro',
  'partner',
  'neutral',
] as const;

export type AskHelixPersonaLensId = (typeof ASK_HELIX_PERSONA_LENS_IDS)[number];

export type AskHelixPersonaLensDescriptor = {
  id: AskHelixPersonaLensId;
  label: string;
};

export type AskHelixPersonaLensMetadata = {
  personaLens: AskHelixPersonaLensDescriptor;
};

type MetadataCarrier = {
  metadata?: Record<string, unknown> | null;
};

const PERSONA_LENS_LABELS: Record<AskHelixPersonaLensId, string> = {
  ceo: 'CEO lens',
  cfo: 'CFO lens',
  coo: 'COO lens',
  chro: 'CHRO lens',
  partner: 'Partner lens',
  neutral: 'Neutral partner lens',
};

const PROFILE_ROLE_ALIASES: Record<AskHelixPersonaLensId, readonly string[]> = {
  ceo: ['ceo', 'chiefexecutiveofficer'],
  cfo: ['cfo', 'chieffinancialofficer'],
  coo: ['coo', 'chiefoperatingofficer'],
  chro: ['chro', 'chiefhumanresourcesofficer', 'chiefpeopleofficer'],
  partner: [
    'partner',
    'executive',
    'advisor',
    'seniorpartner',
    'managingpartner',
    'boardadvisor',
    'boardmember',
    'chair',
    'chairman',
    'chairperson',
  ],
  // `neutral` is the pure machine fallback for opaque/invalid overlay ids
  // (see normalizeAskHelixPersonaLensId); it is not a login-role target.
  neutral: ['neutral'],
};

const VALID_PERSONA_LENS_IDS = new Set<string>(ASK_HELIX_PERSONA_LENS_IDS);

function normalizeRoleToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeAskHelixPersonaLensId(
  value: unknown,
): AskHelixPersonaLensId {
  const id = String(value ?? '').trim().toLowerCase();
  return VALID_PERSONA_LENS_IDS.has(id) ? (id as AskHelixPersonaLensId) : 'neutral';
}

export function resolveAskHelixPersonaLensIdFromProfileRole(
  profileRole: unknown,
): AskHelixPersonaLensId {
  const token = normalizeRoleToken(profileRole);
  // Absent or unrecognized login roles resolve to the general external-advisor
  // `partner` lens (the board-adjacent default view), not the thin machine
  // fallback. `neutral` remains reserved for opaque/invalid overlay ids.
  if (!token) return 'partner';

  for (const lensId of ASK_HELIX_PERSONA_LENS_IDS) {
    if (PROFILE_ROLE_ALIASES[lensId].includes(token)) {
      return lensId;
    }
  }

  return 'partner';
}

export function describeAskHelixPersonaLens(
  lensId: unknown,
): AskHelixPersonaLensDescriptor {
  const id = normalizeAskHelixPersonaLensId(lensId);
  return {
    id,
    label: PERSONA_LENS_LABELS[id],
  };
}

// Concise, executive-facing voice directives for the landing brief synthesizer.
// The brief composes a tightly guard-railed system prompt (not the full governed
// narration overlay), so each lens contributes one voice line here. `neutral`
// (and anything unrecognized) returns null so the brief keeps today's exact voice.
const PERSONA_BRIEF_VOICE_DIRECTIVES: Record<AskHelixPersonaLensId, string | null> = {
  ceo:
    'Write in the voice of the chief executive: enterprise value, the two or ' +
    'three moves that compound, and the durable competitive thesis.',
  cfo:
    'Write in the voice of the chief financial officer: the operating-income ' +
    'bridge, margin and cash, capital discipline, and hard-dollar defensibility.',
  coo:
    'Write in the voice of the chief operating officer: the operating model, ' +
    'cost-to-serve and productivity, execution risk, and service safeguards.',
  chro:
    'Write in the voice of the chief human resources officer: organization ' +
    'design, spans and layers, talent and retention risk, and change absorption.',
  partner:
    'Write in the voice of the trusted external advisor: synthesis across the ' +
    'functions, explicit tradeoffs, and one board-ready recommendation.',
  neutral: null,
};

/**
 * Returns the concise brief voice directive for a selected lens, or null when the
 * lens is neutral/absent/unknown (in which case the brief keeps its baseline voice).
 * Only recognized ids ever change the brief prompt — this keeps the brief path
 * fully fail-safe.
 */
export function askHelixBriefPersonaVoiceDirective(lensId: unknown): string | null {
  const raw = String(lensId ?? '').trim().toLowerCase();
  if (!VALID_PERSONA_LENS_IDS.has(raw)) return null;
  return PERSONA_BRIEF_VOICE_DIRECTIVES[raw as AskHelixPersonaLensId];
}

export function withAskHelixPersonaLensMetadata<T extends MetadataCarrier>(
  response: T,
  lensId: unknown,
): T & { metadata: Record<string, unknown> & AskHelixPersonaLensMetadata } {
  const metadata = isPlainRecord(response.metadata) ? response.metadata : {};
  return {
    ...response,
    metadata: {
      ...metadata,
      personaLens: describeAskHelixPersonaLens(lensId),
    },
  };
}
